import React, { useState, useMemo } from 'react';
import { DashboardHeader } from './DashboardHeader';
import { KPICards } from './KPICards';
import { PerformanceCharts } from './PerformanceCharts';
import { HoldingsTable } from './HoldingsTable';
import { useUser } from '@/contexts/UserContext';
import { useHoldings } from '@/hooks/useHoldings';

/** Return the start-of-range Date for each time-range label.
 *  All returned Dates are normalized to LOCAL midnight so comparisons
 *  against acquisition_date (also parsed to local midnight) are exact
 *  on the start-day boundary. */
function getTimeRangeStart(
  range: string,
  customStart?: Date | null,
): Date | null {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  switch (range) {
    case 'Last 3M':
      return new Date(y, m - 3, d);
    case '6M':
      return new Date(y, m - 6, d);
    case 'YTD':
      return new Date(y, 0, 1); // Jan 1 of current year
    case '12M':
      return new Date(y - 1, m, d);
    case 'Custom':
      return customStart ?? null;
    default:
      return null; // "All" or anything else → no filter
  }
}

export const Dashboard = () => {
  const { currentUser, accountType } = useUser();
  const [selectedAthleteId, setSelectedAthleteId] = useState<string>('');
  const { holdings, loading, error, fetchHoldings, summary } = useHoldings(selectedAthleteId);
  const [selectedTimeRange, setSelectedTimeRange] = useState('12M');
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['All']);
  const [searchQuery, setSearchQuery] = useState('');
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({ from: null, to: null });

  const toggleFilter = (filter: string) => {
    if (filter === 'All') {
      setSelectedFilters(['All']);
    } else {
      const newFilters = selectedFilters.includes(filter)
        ? selectedFilters.filter(f => f !== filter)
        : [...selectedFilters.filter(f => f !== 'All'), filter];

      setSelectedFilters(newFilters.length === 0 ? ['All'] : newFilters);
    }
  };

  // ── Filtered holdings ──────────────────────────────────────────────
  // Filter the raw holdings by time range, holding-type badges, and
  // search query so every downstream component gets the same view.

  // Parse a "YYYY-MM-DD" date string as LOCAL midnight so it matches
  // the Date objects produced by the Calendar component (also local).
  // new Date("2025-08-14") would parse as UTC midnight, causing
  // off-by-one issues in timezones behind UTC.
  const parseLocalDate = (dateStr: string): Date => {
    const parts = dateStr.split('T')[0].split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  };

  const filteredHoldings = useMemo(() => {
    let result = holdings;

    // Time-range filter (by acquisition_date)
    const rangeStart = getTimeRangeStart(selectedTimeRange, customDateRange.from);
    if (rangeStart) {
      const startTime = rangeStart.getTime();
      result = result.filter(h => {
        const acqTime = parseLocalDate(h.acquisition_date).getTime();
        return acqTime >= startTime;
      });
    }
    // Custom end-date cap. Both customDateRange.to and parseLocalDate(acq)
    // are at LOCAL midnight of their respective days, so a direct <=
    // comparison correctly includes acquisitions on the end day and
    // excludes the day after.
    if (selectedTimeRange === 'Custom' && customDateRange.to) {
      const endTime = customDateRange.to.getTime();
      result = result.filter(h => {
        const acqTime = parseLocalDate(h.acquisition_date).getTime();
        return acqTime <= endTime;
      });
    }

    // Holding-type filter (badges: Investment, Content, Social, …)
    if (!selectedFilters.includes('All')) {
      result = result.filter(h =>
        selectedFilters.includes(h.holding_type),
      );
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(h =>
        h.holding_name.toLowerCase().includes(q) ||
        h.holding_type.toLowerCase().includes(q),
      );
    }

    return result;
  }, [holdings, selectedTimeRange, customDateRange, selectedFilters, searchQuery]);

  // ── Recalculate summary from filtered holdings ─────────────────────
  const filteredSummary = useMemo(() => {
    const totalValue = filteredHoldings.reduce((s, h) => s + h.current_value, 0);
    const totalInitialValue = filteredHoldings.reduce((s, h) => s + h.initial_value, 0);
    const totalROI = totalInitialValue > 0
      ? ((totalValue - totalInitialValue) / totalInitialValue) * 100
      : 0;
    const activeHoldings = filteredHoldings.filter(h => h.status === 'Active').length;
    return {
      totalValue,
      totalInitialValue,
      totalROI,
      activeHoldings,
      totalHoldings: filteredHoldings.length,
    };
  }, [filteredHoldings]);

  return (
    <div className="space-y-6">
      <DashboardHeader
        selectedTimeRange={selectedTimeRange}
        setSelectedTimeRange={setSelectedTimeRange}
        selectedFilters={selectedFilters}
        toggleFilter={toggleFilter}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedAthleteId={selectedAthleteId}
        setSelectedAthleteId={setSelectedAthleteId}
        customDateRange={customDateRange}
        setCustomDateRange={setCustomDateRange}
      />

      <KPICards timeRange={selectedTimeRange} summary={filteredSummary} loading={loading} />

      <PerformanceCharts timeRange={selectedTimeRange} holdings={filteredHoldings} loading={loading} />

      <HoldingsTable holdings={filteredHoldings} loading={loading} onRefresh={fetchHoldings} />
    </div>
  );
};
